import { Box, Chip, LinearProgress, Typography } from '@material-ui/core';

/**
 * Below this share of rule weight with data, a score says little about the
 * component: one that has never been collected scores 0 with coverage 0
 * until its first collection.
 */
export const LOW_COVERAGE_THRESHOLD = 0.5;

/** True when too little data was available for the score to be trusted. */
export function isLowCoverage(coverage: number): boolean {
  return coverage < LOW_COVERAGE_THRESHOLD;
}

const NO_SCORE = (
  <Typography variant="body2" color="textSecondary" aria-label="No score">
    —
  </Typography>
);

const NO_LEVEL = (
  <Typography variant="body2" color="textSecondary" aria-label="No level">
    —
  </Typography>
);

/**
 * A 0-100 score as a bar with the rounded number beside it. A score built
 * from no data is shown as "No data yet", never as a bare 0, and one built
 * from less than half the data carries a "low confidence" label. The meaning
 * is always in text, not only in how the bar looks.
 */
export function ScoreBar({
  score,
  coverage,
}: {
  score: number | null;
  coverage: number;
}) {
  if (score === null) {
    return NO_SCORE;
  }
  if (coverage <= 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No data yet
      </Typography>
    );
  }
  const rounded = Math.round(score);
  const lowConfidence = isLowCoverage(coverage);
  return (
    <Box>
      <Box display="flex" alignItems="center" style={{ gap: 8, minWidth: 140 }}>
        <Box flexGrow={1} style={lowConfidence ? { opacity: 0.5 } : undefined}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, score))}
            aria-label={`Score ${rounded} out of 100`}
          />
        </Box>
        <Typography
          variant="body2"
          style={{ minWidth: 28, textAlign: 'right' }}
        >
          {rounded}
        </Typography>
      </Box>
      {lowConfidence && (
        <Typography variant="caption" color="textSecondary">
          Low confidence ({Math.round(coverage * 100)}% of data)
        </Typography>
      )}
    </Box>
  );
}

/** The level reached; provisional when little data backs it, blank with none. */
export function LevelChip({
  level,
  coverage,
}: {
  level: string | null;
  coverage: number;
}) {
  if (!level || coverage <= 0) {
    return NO_LEVEL;
  }
  return isLowCoverage(coverage) ? (
    <Chip size="small" variant="outlined" label={`${level} (provisional)`} />
  ) : (
    <Chip size="small" label={level} />
  );
}
